import { validateLoginFields } from './loginValidation';

export function clearPersistedAuthArtifacts() {
  const shouldRemove = (key) => /^sb-.*-(auth-token|code-verifier)$/.test(String(key || ''))
  const sweep = (storage) => {
    try {
      const keys = []
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i)
        if (shouldRemove(key)) keys.push(key)
      }
      keys.forEach((key) => storage.removeItem(key))
    } catch {}
  }

  if (typeof window === 'undefined') return
  sweep(window.localStorage)
  sweep(window.sessionStorage)
}

export async function clearLocalSession(supabase) {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {}
  clearPersistedAuthArtifacts()
}

// O login do Supabase devolve o MESMO erro para "e-mail não cadastrado" e para
// "senha errada". Esta verificação consulta o banco para separar os dois casos.
// Retorna null quando não foi possível confirmar (ex.: falha de rede), para manter
// a mensagem genérica nesse cenário.
async function isEmailRegistered(supabase, email) {
  try {
    const { data, error } = await supabase.rpc('is_email_registered', { p_email: email });
    if (error) return null;
    return typeof data === 'boolean' ? data : null;
  } catch {
    return null;
  }
}

async function buildInvalidCredentialsError(supabase, email) {
  const registered = await isEmailRegistered(supabase, email);
  if (registered === false) {
    const err = new Error('E-mail não cadastrado. Procure o RH da sua empresa para solicitar seu acesso.');
    err.code = 'email_not_registered';
    return err;
  }
  const err = new Error('Credenciais inválidas.');
  err.code = 'invalid_credentials';
  return err;
}

export async function loginWithPassword(supabase, { email, password }) {
  const { ok, errors, email: e, password: p } = validateLoginFields({ email, password });
  if (!ok) {
    const err = new Error('Campos inválidos.');
    err.code = 'validation';
    err.fields = errors;
    throw err;
  }

  // Garante login limpo quando houver sessão corrompida em storage.
  await clearLocalSession(supabase);

  const { data: signData, error: signErr } = await supabase.auth.signInWithPassword({ email: e, password: p });
  if (signErr) {
    const status = String(signErr?.status || '');
    if (status === '429') {
      const err = new Error('Muitas tentativas. Tente novamente mais tarde.');
      err.code = 'rate_limit';
      throw err;
    }

    const raw = String(signErr?.message || signErr?.error_description || signErr?.error || '').toLowerCase();
    if (raw.includes('email not confirmed') || raw.includes('email not verified') || raw.includes('confirm your email')) {
      const err = new Error('Seu e-mail ainda não foi confirmado. Verifique sua caixa de entrada e confirme antes de entrar.');
      err.code = 'email_not_confirmed';
      throw err;
    }
    if (raw.includes('banned') || raw.includes('disabled')) {
      const err = new Error('Esta conta está suspensa. Entre em contato com o suporte.');
      err.code = 'user_banned';
      throw err;
    }
    if (raw.includes('invalid login credentials') || raw.includes('invalid_credentials') || raw.includes('wrong password')) {
      throw await buildInvalidCredentialsError(supabase, e);
    }
    if (status === '400' || status === '401') {
      throw await buildInvalidCredentialsError(supabase, e);
    }
    if (raw.includes('network') || raw.includes('fetch failed') || raw.includes('failed to fetch') || raw.includes('connection')) {
      const err = new Error('Falha de conexão com o servidor. Tente novamente.');
      err.code = 'network_error';
      throw err;
    }

    // Erro inesperado: registra o detalhe real para diagnóstico, sem expor detalhes ao usuário.
    console.error('[authFlow] login error inesperado:', signErr);
    const err = new Error('Falha ao autenticar. Tente novamente.');
    err.code = 'unknown';
    throw err;
  }

  const session = signData?.session || null;
  if (!session?.access_token || !session?.refresh_token || !signData?.user?.id) {
    await clearLocalSession(supabase);
    const err = new Error('Falha ao criar sessão válida.');
    err.code = 'invalid_session';
    throw err;
  }

  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData?.user?.id) {
    await clearLocalSession(supabase);
    const err = new Error('Falha ao validar sessão após o login.');
    err.code = 'invalid_session';
    throw err;
  }

  return { session, user: userData.user };
}

export async function logout(supabase) {
  const { error } = await supabase.auth.signOut();
  if (error) {
    await clearLocalSession(supabase);
    return;
  }
}

export async function requestPasswordReset(supabase, { email, redirectTo } = {}) {
  const e = String(email || '').trim()
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
  if (!e) {
    const err = new Error('Informe o e-mail.')
    err.code = 'validation'
    err.fields = { email: 'Informe o e-mail.' }
    throw err
  }
  if (!isValidEmail(e)) {
    const err = new Error('E-mail inválido.')
    err.code = 'validation'
    err.fields = { email: 'E-mail inválido.' }
    throw err
  }

  const options = {}
  if (typeof redirectTo === 'string' && redirectTo.trim()) {
    options.redirectTo = redirectTo.trim()
  } else if (typeof window !== 'undefined' && window.location?.origin) {
    options.redirectTo = `${window.location.origin}/reset`
  }

  const { error } = await supabase.auth.resetPasswordForEmail(e, options)

  if (error) {
    const status = error?.status
    if (status === 429) {
      const err = new Error('Muitas tentativas. Tente novamente mais tarde.')
      err.code = 'rate_limit'
      throw err
    }
    throw error
  }
  return true
}
